import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { SkeletonButton, SkeletonBadge, SkeletonText, SkeletonTitle } from '@/components/ui/skeleton-base';
import { Skeleton } from '@/components/ui/skeleton';

const ProcessorHeaderSkeleton = () => (
    <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-10" />
            <SkeletonTitle className="w-64" />
        </div>
        <div className="flex gap-2">
            <SkeletonButton />
            <SkeletonButton />
        </div>
    </div>
);

const OverviewCardSkeleton = () => (
    <Card className="mb-6">
        <CardHeader>
            <CardTitle>
                <SkeletonText className="w-24" />
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                    <Skeleton className="h-12 w-12" />
                    <div className="flex-1">
                        <SkeletonText className="mb-1 w-28" />
                        <SkeletonText className="w-36" />
                    </div>
                </div>
                <div>
                    <SkeletonText className="mb-1 w-16" />
                    <SkeletonBadge />
                </div>
                <div>
                    <SkeletonText className="mb-1 w-20" />
                    <SkeletonBadge />
                </div>
                <div>
                    <SkeletonText className="mb-1 w-24" />
                    <SkeletonText className="w-32" />
                </div>
            </div>
        </CardContent>
    </Card>
);

const MetricsCardSkeleton = () => (
    <Card className="mb-6">
        <CardHeader>
            <CardTitle>
                <SkeletonText className="w-44" />
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                        <SkeletonText className="mb-2 w-32" />
                        <Skeleton className="h-8 w-24" />
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

const ConfigurationCardSkeleton = () => (
    <Card className="mb-6">
        <CardHeader>
            <CardTitle>
                <SkeletonText className="w-40" />
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i}>
                        <SkeletonText className="mb-1 w-28" />
                        <SkeletonText className="w-36" />
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

const AccountDetailsCardSkeleton = () => (
    <Card className="mb-6">
        <CardHeader>
            <CardTitle>
                <SkeletonText className="w-44" />
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i}>
                            <SkeletonText className="mb-1 w-32" />
                            <SkeletonText className="w-28" />
                        </div>
                    ))}
                </div>
                <div>
                    <SkeletonText className="mb-3 w-24" />
                    <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="flex items-center justify-between">
                                <SkeletonText className="w-40" />
                                <SkeletonBadge />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>
);

const OptionsCardSkeleton = () => (
    <Card className="mb-6">
        <CardHeader>
            <CardTitle>
                <SkeletonText className="w-28" />
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-start gap-4 pb-4 border-b last:border-0">
                        <div className="flex-1">
                            <SkeletonText className="mb-1 w-32" />
                            <SkeletonText className="w-48" />
                        </div>
                    </div>
                ))}
            </div>
        </CardContent>
    </Card>
);

export const StripeProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
        <AccountDetailsCardSkeleton />
    </>
);

export const GenericProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <OptionsCardSkeleton />
    </>
)

export const NMIProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
        <OptionsCardSkeleton />
    </>
);

export const MastercardProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
        <OptionsCardSkeleton />
    </>
);

export const AirwallexProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
        <OptionsCardSkeleton />
    </>
);

export const CheckoutProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
    </>
);

export const TagadaPayProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
        <OptionsCardSkeleton />
    </>
);

export const SandboxProcessorDetailsSkeleton = () => (
    <>
        <ProcessorHeaderSkeleton />
        <OverviewCardSkeleton />
        <MetricsCardSkeleton />
        <ConfigurationCardSkeleton />
        <OptionsCardSkeleton />
        <OptionsCardSkeleton />
        <OptionsCardSkeleton />
    </>
);

